<script setup lang="ts">
const route = useRoute()
const { t } = useI18n({ useScope: 'local' })
const localePath = useLocalePath()

const { data } = useAsyncData(`collections/${route.params.condition}`, async () => {
  const { apps } = await queryContent('_collections', 'medication-adherence')
    .findOne()

  return (apps as Collection[])
    .filter(c => c.conditions.includes(route.params.condition as string))
})

const lang = computed(() => {
  const { lang } = route.query
  return lang ? lang as string : 'all'
})

const platform = computed(() => {
  const { platform } = route.query
  return platform ? platform as 'iOS' | 'Android' : 'all'
})

const collections = computed(() => {
  if (!data.value)
    return []

  return data.value
    .filter((item) => {
      if (lang.value === 'all')
        return true

      return item.app.languages.includes(lang.value)
    })
    .filter((item) => {
      if (platform.value === 'all')
        return true

      return !!item.app.uid[platform.value]
    })
})

useHead(useSeo({
  title: t(`${route.params.condition}.meta.title`),
  description: t(`${route.params.condition}.meta.description`),
  image: {
    url: t(`${route.params.condition}.meta.image`),
  },
}))
</script>

<template>
  <div>
    <NuxtLayout name="collections">
      <main>
        <FeaturesCollectionsSectionHero
          :title="t(`${route.params.condition}.title`)"
          :description="t(`${route.params.condition}.description`)"
          :collections="collections"
          :back="localePath('resources-collections-medication-adherence')"
        >
          <template v-if="route.params.condition === 'medication-adherence'" #icon>
            <IconCollectionsMedicationAdherenceHealth class="w-10 h-10 mb-4" />
          </template>
          <template v-else-if="route.params.condition === 'mental-health'" #icon>
            <IconCollectionsMedicationAdherenceMoodDepression class="w-10 h-10 mb-4" />
          </template>
          <template v-else-if="route.params.condition === 'diabetes'" #icon>
            <IconCollectionsMedicationAdherenceDiabetes class="w-10 h-10 mb-4" />
          </template>
          <template v-else-if="route.params.condition === 'hypertension'" #icon>
            <IconCollectionsMedicationAdherenceHypertension class="w-10 h-10 mb-4" />
          </template>
          <template v-else-if="route.params.condition === 'asthma'" #icon>
            <IconCollectionsMedicationAdherenceAsthme class="w-10 h-10 mb-4" />
          </template>
        </FeaturesCollectionsSectionHero>

        <div class="flex flex-wrap items-center justify-center gap-3 px-4 mt-10 md:gap-8 md:mt-12">
          <FeaturesCollectionsSectionFilterPlatform class="w-full md:w-auto" />
          <FeaturesCollectionsSectionFilterLanguages class="w-full md:w-auto" />
        </div>

        <div class="grid px-4 mx-auto mt-12 md:mt-20 gap-y-24 md:gap-y-36 max-w-7xl">
          <FeaturesCollectionsSectionApplication
            v-for="collection in collections"
            :key="collection.id"
            :collection="collection"
          />
        </div>
      </main>
    </NuxtLayout>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "medication-adherence": {
      "meta": {
        "title": "Medication Adherence, Medication Management",
        "description": "This app selection will allow you to track your medication use or provide insights on your medication",
        "image": "/assets/meta/collections/appguide-medication-adherence-en-c1.jpg"
      },
      "title": "Better manage all of your medications",
      "description": "This app selection will allow you to track your medication use or provide insights on your medication"
    },
    "mental-health": {
      "meta": {
        "title": "Medication Adherence, Mental Health",
        "description": "This app selection will allow you to better understand and manage drug therapies for mental health.",
        "image": "/assets/meta/collections/appguide-medication-adherence-en-c2.jpg"
      },
      "title": "Better manage treatments for mental health",
      "description": "This app selection will allow you to better understand and manage drug therapies for mental health."
    },
    "diabetes": {
      "meta": {
        "title": "Medication Adherence, Diabetes",
        "description": "This app selection will allow you to better understand and manage drug therapies for diabetes.",
        "image": "/assets/meta/collections/appguide-medication-adherence-en-c3.jpg"
      },
      "title": "Better manage treatments for diabetes",
      "description": "This app selection will allow you to better understand and manage drug therapies for diabetes."
    },
    "hypertension": {
      "meta": {
        "title": "Medication Adherence, Hypertension",
        "description": "This app selection will allow you to better understand and manage drug therapies for hypertension.",
        "image": "/assets/meta/collections/appguide-medication-adherence-en-c4.jpg"
      },
      "title": "Better manage treatments for hypertension",
      "description": "This app selection will allow you to better understand and manage drug therapies for hypertension."
    },
    "asthma": {
      "meta": {
        "title": "Medication Adherence, Asthma",
        "description": "This app selection will allow you to better understand and manage drug therapies for asthma.",
        "image": "/assets/meta/collections/appguide-medication-adherence-en-c5.jpg"
      },
      "title": "Better manage treatments for asthma",
      "description": "This app selection will allow you to better understand and manage drug therapies for asthma."
    }
  },
  "fr": {
    "medication-adherence": {
      "meta": {
        "title": "Prise de médicaments, Gestion des médicaments",
        "description": "Cette sélection d’applis vous permettra de suivre votre médication ou vous fournir des informations sur vos médicaments",
        "image": "/assets/meta/collections/appguide-medication-adherence-fr-c1.jpg"
      },
      "title": "Mieux gérer l’ensemble de vos médicaments",
      "description": "Cette sélection d’applis vous permettra de suivre votre médication ou vous fournir des informations sur vos médicaments"
    },
    "mental-health": {
      "meta": {
        "title": "Prise de médicaments, Santé mentale",
        "description": "Cette sélection d’applis vous permettra de mieux comprendre et gérer les traitements médicamenteux en lien avec la santé mentale.",
        "image": "/assets/meta/collections/appguide-medication-adherence-fr-c2.jpg"
      },
      "title": "Mieux gérer les traitements pour la santé mentale",
      "description": "Cette sélection d’applis vous permettra de mieux comprendre et gérer les traitements médicamenteux en lien avec la santé mentale."
    },
    "diabetes": {
      "meta": {
        "title": "Prise de médicaments, Diabète",
        "description": "Cette sélection d’applis vous permettra de mieux comprendre et gérer les traitements médicamenteux en lien avec le diabète.",
        "image": "/assets/meta/collections/appguide-medication-adherence-fr-c3.jpg"
      },
      "title": "Mieux gérer les traitements pour le diabète",
      "description": "Cette sélection d’applis vous permettra de mieux comprendre et gérer les traitements médicamenteux en lien avec le diabète."
    },
    "hypertension": {
      "meta": {
        "title": "Prise de médicaments, Hypertension",
        "description": "Cette sélection d’applis vous permettra de mieux comprendre et gérer les traitements médicamenteux en lien avec l’hypertension.",
        "image": "/assets/meta/collections/appguide-medication-adherence-fr-c4.jpg"
      },
      "title": "Mieux gérer les traitements pour l’hypertension",
      "description": "Cette sélection d’applis vous permettra de mieux comprendre et gérer les traitements médicamenteux en lien avec l’hypertension."
    },
    "asthma": {
      "meta": {
        "title": "Prise de médicaments, Asthme",
        "description": "Cette sélection d’applis vous permettra de mieux comprendre et gérer les traitements médicamenteux en lien avec l’asthme.",
        "image": "/assets/meta/collections/appguide-medication-adherence-fr-c5.jpg"
      },
      "title": "Mieux gérer les traitements pour l’asthme",
      "description": "Cette sélection d’applis vous permettra de mieux comprendre et gérer les traitements médicamenteux en lien avec l’asthme."
    }
  }
}
</i18n>
